/**
 * BackgroundTaskBar – Floating task-progress toasts (WhatsApp/Gmail style).
 *
 * Renders in the bottom-right corner, separate from the notification toasts.
 * Shows each pending/success/error task with spinners, check marks, or retry.
 */

import React, { useState } from 'react';
import { CheckCircle2, XCircle, RotateCcw, Loader2, X } from 'lucide-react';
import { cn } from './cn';
import { useBackgroundTask } from '../hooks/useBackgroundTask';

export function BackgroundTaskBar() {
  const { tasks, retry } = useBackgroundTask();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const visible = tasks.filter(t => !dismissed.has(t.id));
  if (visible.length === 0) return null;

  return (
    <div className="fixed top-[72px] right-4 z-[9998] flex flex-col gap-2 pointer-events-none max-w-xs w-full">
      {visible.map(task => (
        <div
          key={task.id}
          className={cn(
            'pointer-events-auto flex items-center gap-2.5 pl-3 pr-2 py-2.5 rounded-xl border shadow-lg backdrop-blur-sm transition-all duration-300',
            task.status === 'pending' && 'bg-white/95 border-slate-200',
            task.status === 'success' && 'bg-emerald-50/95 border-emerald-200',
            task.status === 'error'   && 'bg-red-50/95 border-red-200',
          )}
        >
          {/* Status icon */}
          {task.status === 'pending' && (
            <Loader2 size={16} className="text-indigo-500 animate-spin shrink-0" />
          )}
          {task.status === 'success' && (
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
          )}
          {task.status === 'error' && (
            <XCircle size={16} className="text-red-500 shrink-0" />
          )}

          {/* Label */}
          <div className="flex-1 min-w-0">
            <p className={cn(
              'text-xs font-semibold truncate',
              task.status === 'pending' && 'text-slate-700',
              task.status === 'success' && 'text-emerald-700',
              task.status === 'error'   && 'text-red-700',
            )}>
              {task.status === 'pending' && `Saving ${task.label}…`}
              {task.status === 'success' && `${task.label} saved`}
              {task.status === 'error'   && `Failed to save ${task.label}`}
            </p>
          </div>

          {/* Retry button for errors */}
          {task.status === 'error' && (
            <button
              onClick={() => retry(task.id)}
              className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-red-600 bg-red-100 hover:bg-red-200 transition-colors"
            >
              <RotateCcw size={10} /> Retry
            </button>
          )}

          {/* Dismiss */}
          {task.status !== 'pending' && (
            <button
              onClick={() => setDismissed(prev => new Set(prev).add(task.id))}
              className="shrink-0 p-0.5 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X size={12} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
